import { unref, watch, isRef, nextTick, isReactive, Ref } from 'vue';
import { cloneDeep, set, get } from 'lodash';
import { v4 } from 'uuid';

interface FormField {
    instance: InstanceType<any>,
    required: boolean,
    validation: () => true|string,
    value: any,
    emitInvalid: boolean,
}

export class Form {
    fields: { [key: string]: FormField };

    formData: { [key: string]: any };

    onSubmit: () => void;

    uuid: string;

    defaultFormData: { [key: string]: any };

    formElementCreated: boolean;

    submittedFields: Array<string> = [];

    constructor(formData: { [key: string]: any }, onSubmit: () => void) {
        this.fields = {};
        this.defaultFormData = cloneDeep(formData);
        this.formData = formData;
        this.onSubmit = onSubmit;
        this.uuid = v4();
        this.formElementCreated = false;
    }

    async addField(
        name: string,
        instance: InstanceType<any>,
        required: Ref<boolean>|boolean,
        validation: () => string|true,
        value: any,
        emitInvalid: boolean,
        disabled: boolean,
    ) {
        watch(value, () => {
            this.submittedFields = [];
            if (validation() !== true && !emitInvalid) {
                return;
            }
            if (disabled) {
                return;
            }
            set(this.formData, name, unref(value));
        }, { deep: true });
        watch(this.formData, () => {
            this.submittedFields = [];
            if (isRef(value)) {
                value.value = get(this.formData, name);
            } else if (isReactive(value)) {
                Object.assign(value, get(this.formData, name));
            } else {
                value = get(this.formData, name);
            }
        }, { immediate: true, deep: true });
        if (isRef(required)) {
            watch(required, () => {
                this.fields[name].required = unref(required);
            });
        }

        await nextTick();
        this.fields[name] = {
            instance,
            required: unref(required),
            validation,
            value,
            emitInvalid,
        };
    }

    removeField(name: string) {
        delete this.fields[name];
        if (Object.values(this.fields).length === 0) {
            this.formElementCreated = false;
        }
    }

    reset() {
        Object.assign(this.formData, this.defaultFormData);
        for (const field in this.fields) {
            this.fields[field].instance.exposed.reset();
        }
    }

    async submit(instances: Array<string> = []) {
        await nextTick();
        this.submittedFields = [...new Set([...this.submittedFields, ...instances])];
        const fieldNames = Object.keys(this.fields);
        for (const fieldName of fieldNames) {
            const field = this.fields[fieldName];
            if (this.submittedFields.includes(fieldName)) {
                continue;
            }

            // eslint-disable-next-line no-await-in-loop
            const result = await Promise.resolve(field.instance.exposed.submit());
            if (!result) {
                this.submittedFields = [];
            }

            return result;
        }

        this.submittedFields = [];
        await this.onSubmit();
        return true;
    }
}
